import { ClipboardList, Folder, FolderOpen, ListTodo } from "lucide-react";
import type { FolderVariant } from "../../types";
import { cn } from "@/lib/utils";
import React from "react";

interface FolderIconProps {
  /** Whether the folder is open */
  isOpen: boolean;
  /**
   * "brief": the project's Brief folder, drawn with the Brief nav item's clipboard.
   * "issue-attachments": the Issue Attachments folder, drawn with the Issues nav item's list.
   */
  variant?: "folder" | FolderVariant;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FolderIcon - Atom component
 *
 * Displays folder or folder-open icon based on open state with semantic colors.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderIcon isOpen={false} />
 * <FolderIcon isOpen={true} className="h-5 w-5" />
 * ```
 */
export const FolderIcon = React.memo(function FolderIcon({
  isOpen,
  variant = "folder",
  className,
}: FolderIconProps) {
  const Icon =
    variant === "brief"
      ? ClipboardList
      : variant === "issue-attachments"
        ? ListTodo
        : isOpen
          ? FolderOpen
          : Folder;

  return (
    <Icon
      className={cn(
        "h-4 w-4 text-red-500 transition-all duration-200",
        isOpen && "drop-shadow-sm",
        className
      )}
      aria-hidden="true"
    />
  );
});
